import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import "./CommandsParams.css";
import { TextField, Tooltip } from "@mui/material";
import { CommandContextStore } from "../context/CommandContext";
import {
  FormProvider,
  useFormContext,
  Controller,
  useForm,
} from "react-hook-form";
import React, { memo } from "react";

const ParamInputText = ({ param }) => {
  const { watch, register } = useFormContext();
  return (
    <div
      style={{
        display: "flex",
        paddingTop: 6,
        paddingLeft: 6,
        margin: "10px",
      }}
    >
      {param.optional && (
        <Checkbox
          {...register(param.name + "_selected")}
          checked={watch(param.name + "_selected")}
        />
      )}
      <Tooltip title={param.description} style={{ paddingTop: 2 }}>
        <TextField
          {...register(param.name)}
          name={param.name}
          defaultValue={param.default}
          label={param.name}
          fullWidth
          className="inputParam"
          size="small"
          // InputLabelProps={{shrink: !!field.value}}
        />
      </Tooltip>
    </div>
  );
};

const NestedInputText = memo(
  ({ register, control, watch, formState: { isDirty }, param }) => (
    <div
      style={{
        display: "flex",
        paddingTop: 6,
        paddingLeft: 6,
        margin: "10px",
      }}
    >
      {param.optional && (
        <Controller
          name={param.name + "_selected"}
          control={control}
          render={({ field }) => {
            return (
              <Checkbox
                className="inputParam"
                size="small"
                {...field}
                checked={watch(param.name + "_selected")}
              />
            );
          }}
        />
      )}
      <Tooltip title={param.description} style={{ paddingTop: 2 }}>
        <TextField
          {...register(param.name)}
          name={param.name}
          defaultValue={param.default}
          label={param.name}
          fullWidth
          className="inputParam"
          size="small"
          // InputLabelProps={{shrink: !!field.value}}
        />
      </Tooltip>
    </div>
  ),
  (prevProps, nextProps) =>
    prevProps.formState.isDirty === nextProps.formState.isDirty
);

const NestedInputSelect = memo(
  ({
    register,
    setValue,
    getValues,
    watch,
    control,
    formState: { isDirty },
    param,
  }) => (
    <div
      style={{
        display: "flex",
        paddingTop: 6,
        paddingLeft: 6,
        margin: "10px",
      }}
    >
      {param.optional && (
        <Controller
          name={param.name + "_selected"}
          control={control}
          render={({ field }) => {
            return (
              <Checkbox
                className="inputParam"
                size="small"
                {...field}
                checked={watch(param.name + "_selected")}
              />
            );
          }}
        />
      )}

      <Controller
        name={param.name}
        control={control}
        render={({ field }) => {
          return (
            <Tooltip title={param.description} style={{ paddingTop: 2 }}>
              <FormControl fullWidth>
                <InputLabel id={param.name + "_id"}>{param.name}</InputLabel>
                <Select
                  {...field}
                  fullWidth
                  labelId={param.name + "_id"}
                  label={param.name}
                  className="inputParam"
                  size="small"
                  style={{ textAlign: "left" }}
                >
                  {param.options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
          );
        }}
      />
    </div>
  ),
  (prevProps, nextProps) =>
    prevProps.formState.isDirty === nextProps.formState.isDirty
);

const NestedInputFlag = memo(
  ({ register, control, watch, formState: { isDirty }, param }) => (
    <Tooltip title={param.description} style={{ paddingTop: 2 }}>
      <FormControlLabel
        style={{ paddingLeft: 27, display: "flex" }}
        control={
          <Controller
            name={param.name + "_selected"}
            control={control}
            render={({ field }) => {
              return (
                <Checkbox
                  {...field}
                  className="inputParam"
                  size="small"
                  checked={watch(param.name + "_selected")}
                />
              );
            }}
          />
        }
        label={<Typography variant="checkbox">{param.name}</Typography>}
      />
    </Tooltip>
  ),
  (prevProps, nextProps) =>
    prevProps.formState.isDirty === nextProps.formState.isDirty
);

const NestedLabel = memo(
  ({ formState: { isDirty }, param }) => (
    <FormControlLabel
      style={{ paddingTop: 12, paddingLeft: 20, display: "flex" }}
      control={
        <Controller
          render={() => {
            return null;
          }}
        />
      }
      label={<Typography variant="overline">{param.name}</Typography>}
    />
  ),
  (prevProps, nextProps) =>
    prevProps.formState.isDirty === nextProps.formState.isDirty
);

export const NestedInputContainerText = ({ param }) => {
  const methods = useFormContext();

  return <NestedInputText {...methods} param={param} />;
};

export const NestedInputContainerSelect = ({ param }) => {
  const methods = useFormContext();

  return <NestedInputSelect {...methods} param={param} />;
};

export const NestedInputContainerFlag = ({ param }) => {
  const methods = useFormContext();

  return <NestedInputFlag {...methods} param={param} />;
};

export const NestedContainerLabel = ({ param }) => {
  const methods = useFormContext();

  return <NestedLabel {...methods} param={param} />;
};

const ParamInput = ({ param }) => {
  switch (param.type) {
    case "text":
      return <NestedInputContainerText param={param} />;
    case "select":
      return <NestedInputContainerSelect param={param} />;
    case "flag":
      return <NestedInputContainerFlag param={param} />;
    case "label":
      return <NestedContainerLabel param={param} />;
    default:
      return null;
  }
};

export const ParamsTemplate = () => {
  const methods = useForm();
  const { control, register, watch, setValue, getValues } = methods;
  const [toolParams, setToolsParams] = React.useState([]);
  const [toolSavedParams, setToolSavedParams] = React.useState([]);
  const commandContext = React.useContext(CommandContextStore);
  const watchAllFields = watch();

  const renderParamsCore = (paramsValues) => {
    // Debug
    // console.log("Changing params");
    // Need to render params
    let rendered = {};
    toolParams.map((param) => {
      if (param.template) {
        rendered[param.name] = param.template.format({
          0: paramsValues[param.name],
          ...commandContext.savedParams,
        });
      }
      if (param.type === "flag" && !param.template) {
        rendered[param.name] = param.value;
      }
    });
    // Debug
    //console.log(toolParams);

    let selectedOpt = toolParams
      .filter((p) => !!paramsValues[p.name + "_selected"])
      .map((p) => p.name);
    // Debug
    //console.log(selectedOpt);

    // Update parameteres
    // console.log("update svaed params");
    commandContext.dispatch({
      type: "UPDATE_PARAMS",
      data: {
        rendered: { rendered: rendered, selectedOpt: selectedOpt },
        saved: paramsValues,
      },
    });
  };

  const renderParams = () => {
    // console.log("Enetered Changing params");
    let paramsValues = getValues();
    // Debug
    // console.log(paramsValues);
    // console.log(commandContext.savedParams);

    // Avoid endless loop
    if (JSON.stringify(paramsValues) !== JSON.stringify(toolSavedParams)) {
      setToolSavedParams(paramsValues);
      renderParamsCore(paramsValues);
    }
  };

  // This triggered when param changed using GUI
  React.useEffect(() => {
    // console.log(commandContext);
    const timeOutId = setTimeout(() => {
      // Debug
      // console.log("Watch all fields triggered");
      renderParams();
    }, 300);
    return () => clearTimeout(timeOutId);
  }, [watchAllFields]);

  React.useEffect(() => {
    // Debug
    // console.log("command context params changed");
    const params = structuredClone(commandContext.params);
    setToolsParams(params);
    params.map((param) => {
      setValue(
        param.name,
        commandContext.savedParams[param.name] || param.default
      );
      setValue(
        param.name + "_selected",
        commandContext.savedParams[param.name + "_selected"] || param.selected
      );
    });
    renderParamsCore(getValues());
  }, [commandContext.params]);

  return (
    <div className="params-menu" style={{marginLeft: '1vw'}}>
      <div className="title">Command config</div>
      <FormProvider {...methods}>
        {toolParams.map((param) => {
          return (
            <section key={param.name}>
              <ParamInput param={param} />
            </section>
          );
        })}
      </FormProvider>
    </div>
  );
};
